import React, { useState, useEffect, useRef, useCallback } from "react";

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip as chartTooltip,
	Legend,
	PointElement,
	LineElement,
} from "chart.js";
import { Bar, Line, getElementsAtEvent } from "react-chartjs-2";
// import ChartDataLabels from "chartjs-plugin-datalabels";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";

// Layout/UI
import { Box, Button, HStack, RadioGroup, Stack, Switch, Text, VStack, MetreaTooltip } from "@metrea-ui/v2";

// Utilities
import { mColor, getContrastYIQ } from "src/utils/themeUtils";
import { applyFormatToNumber, applySignToNumber } from "src/utils/formatUtils";
import { findObjectByProperty, getValuesByPropertyPath, filterObjectsByProperty } from "src/utils/dataUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";
import { buildSummaryTable, buildDeltaTable, buildChartTable } from "src/utils/tableBuilders";

const arrayGroupDetails = [
	{
		comp_type_id: 111,
		name: "Military Service",
		allGroupName: "Military Services",
		column: "military_service_id",
		code: "military_service",
	},
	{
		comp_type_id: 112,
		name: "Domain",
		allGroupName: "Domains",
		column: "domain_id",
		code: "domain",
	},
	{
		comp_type_id: 113,
		name: "Category",
		allGroupName: "Categories",
		column: "category_id",
		code: "category",
	},
];

const DISPLAY = true;
const BORDER = true;
const CHART_AREA = true;
const TICKS = true;

// Preserve the old checkbox-style handler contract while wiring these toggles
// directly to the v2 compound Switch API.
const getCheckedValue = (next) => next === true || next?.checked === true || next?.detail?.checked === true;

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	chartTooltip,
	Legend,
	PointElement,
	LineElement,
	// ChartDataLabels
);

const ScenarioBudgetSummary = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const ref = useRef();
	const refDelta = useRef();
	const refSummary = useRef();
	const barRef = useRef();

	useEffect(() => {
		debugLog(2, "[ScenarioBudgetSummary][FLOW] mounted", {});
	}, []);

	const [currScenario, setCurrScenario] = useState(props.item);
	const [chartData, setChartData] = useState({
		labels: ["Red", "Orange", "Blue"],
		// datasets is an array of objects where each object represents a set of data to display corresponding to the labels above. for brevity, we'll keep it at one object
		datasets: [
			{
				label: "Popularity of colours",
				data: [55, 23, 96],
				// you can set indiviual colors for each bar
				backgroundColor: [
					"rgba(255, 200, 200, 0.6)",
					"rgba(200, 255, 200, 0.6)",
					"rgba(200, 200, 255, 0.6)",
					"rgba(200, 200, 255, 0.6)",
					"rgba(200, 200, 255, 0.6)",
				],
				borderWidth: 1,
			},
		],
	});
	const [chartOptions, setChartOptions] = useState({
		responsive: true,
		plugins: {
			legend: {
				display: false,
				position: "bottom",
			},
			title: {
				display: true,
				text: "By Periods",
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						let label = context.dataset.label || "";
						if (label) {
							label += ": ";
						}

						if (context.parsed.y !== null) {
							label += applyFormatToNumber(
								context.parsed.y,
								currScenario.currency_display_decimal,
								currScenario.currency_symbol,
								currScenario.currency_unit,
								true,
							);
						}
						return label;
					},
				},
			},
			width: 10,
		},
		scaleBeginAtZero: true,
		scales: {
			x: {
				border: {
					display: BORDER,
				},
				grid: {
					display: DISPLAY,
					drawOnChartArea: CHART_AREA,
					drawTicks: TICKS,
				},
				ticks: {
					color: mColor.Primary,
				},
			},
			y: {
				border: {
					display: BORDER,
				},
				display: true,
				title: {
					display: true,
					text: "Budget Changes",
					color: mColor.Primary,
					font: {
						size: 16,
						style: "normal",
						lineHeight: 1.2,
					},
					padding: { top: 20, left: 0, right: 0, bottom: 10 },
				},
				grid: {
					color: function (context) {
						if (context.tick.value > 0) {
							return mColor.Danger;
						} else if (context.tick.value < 0) {
							return mColor.Land;
						}
						return mColor.MidnightBlue;
					},
					tickWidth: function (context) {
						if (context.tick.value === 0) return 1;
						else return 0.5;
					},
					lineWidth: function (context) {
						if (context.tick.value === 0) return 1;
						else return 0.5;
					},
				},
				ticks: {
					beginAtZero: true,
					// For a category axis, the val is the index so the lookup via getLabelForValue is needed
					callback: function (val, index) {
						// Hide every 2nd tick label
						// return (index % 2 === 0)
						// 	? applyFormatToNumber(
						// 			this.getLabelForValue(val),
						// 			currScenario.currency_display_decimal,
						// 			currScenario.currency_symbol,
						// 			currScenario.currency_unit,
						// 			true
						// 	  )
						// 	: "";

						// Hide every 2nd tick label, but always show 0
						const ticks = getValuesByPropertyPath(this.ticks, "value");
						const offset0 = ticks.indexOf(0) % 2;
						return index % 2 === offset0
							? applyFormatToNumber(
									this.getLabelForValue(val),
									currScenario.currency_display_decimal,
									currScenario.currency_symbol,
									currScenario.currency_unit,
									true,
								)
							: "";

						// Show all ticks
						// return applyFormatToNumber(
						// 	this.getLabelForValue(val),
						// 	currScenario.currency_display_decimal,
						// 	currScenario.currency_symbol,
						// 	currScenario.currency_unit,
						// 	true
						// );
					},
					color: function (context) {
						if (context.tick.value > 0) {
							return mColor.Danger;
						} else if (context.tick.value < 0) {
							return mColor.Land;
						}
						return mColor.MidnightBlue;
					},
				},
			},
		},
	});
	const [showBiggestDeltas, setShowBiggestDeltas] = useState(false);
	const [deltaChartData, setDeltaChartData] = useState({
		labels: ["Red", "Orange", "Blue"],
		// datasets is an array of objects where each object represents a set of data to display corresponding to the labels above. for brevity, we'll keep it at one object
		datasets: [
			{
				label: "Popularity of colours",
				data: [55, 23, 96],
				// you can set indiviual colors for each bar
				backgroundColor: [
					"rgb(255, 200, 200)",
					"rgb(200, 255, 200)",
					"rgb(200, 200, 255)",
					"rgb(200, 200, 255)",
					"rgb(200, 200, 255)",
				],
				borderWidth: 1,
			},
		],
	});
	const [deltaChartOptions] = useState({
		responsive: true,
		plugins: {
			legend: {
				display: true,
				position: "bottom",
			},
			title: {
				display: true,
				text: "Biggest Deltas",
			},
			tooltip: {
				callbacks: {
					label: function (context) {
						let arrayStr = [];
						let idx = 0;
						let tempStr = context.dataset.label || "";
						if (tempStr) {
							tempStr += ": ";
						}
						arrayStr.push(tempStr);
						arrayStr.push(" ");

						if (context.parsed.y !== null) {
							idx = context.dataset.data.indexOf(context.parsed.y);

							tempStr = "Total Budget Change: ";
							tempStr += applyFormatToNumber(
								context.dataset.budgetTotal,
								currScenario.currency_display_decimal,
								currScenario.currency_symbol,
								currScenario.currency_unit,
								true,
							);
							arrayStr.push(tempStr);
							tempStr = `Total Qantity Change: ${applySignToNumber(
								context.dataset.quantityTotal,
							)} `;
							arrayStr.push(tempStr);
							arrayStr.push(" ");

							tempStr = `Budget Change in ${context.dataset.periods[idx]}: `;
							tempStr += applyFormatToNumber(
								context.parsed.y,
								currScenario.currency_display_decimal,
								currScenario.currency_symbol,
								currScenario.currency_unit,
								true,
							);
							arrayStr.push(tempStr);
							tempStr = `Quantity Change in ${
								context.dataset.periods[idx]
							}: ${applySignToNumber(context.dataset.quantity[idx])}`;
							arrayStr.push(tempStr);
						}

						return arrayStr;
					},
				},
			},
			width: 10,
		},
		scaleBeginAtZero: true,
		scales: {
			x: {
				border: {
					display: BORDER,
				},
				grid: {
					display: DISPLAY,
					drawOnChartArea: CHART_AREA,
					drawTicks: TICKS,
				},
				ticks: {
					color: mColor.Primary,
				},
			},
			y: {
				border: {
					display: BORDER,
				},
				display: true,
				title: {
					display: true,
					text: "Budget Changes",
					color: mColor.Primary,
					font: {
						size: 16,
						style: "normal",
						lineHeight: 1.2,
					},
					padding: { top: 20, left: 0, right: 0, bottom: 10 },
				},
				grid: {
					color: function (context) {
						if (context.tick.value > 0) {
							return mColor.Danger;
						} else if (context.tick.value < 0) {
							return mColor.Land;
						}
						return mColor.MidnightBlue;
					},
					tickWidth: function (context) {
						if (context.tick.value === 0) return 1;
						else return 0.5;
					},
					lineWidth: function (context) {
						if (context.tick.value === 0) return 1;
						else return 0.5;
					},
				},
				ticks: {
					beginAtZero: true,
					// For a category axis, the val is the index so the lookup via getLabelForValue is needed
					callback: function (val, index) {
						// Hide every 2nd tick label, but always show 0
						const ticks = getValuesByPropertyPath(this.ticks, "value");
						const offset0 = ticks.indexOf(0) % 2;
						return index % 2 === offset0
							? applyFormatToNumber(
									this.getLabelForValue(val),
									currScenario.currency_display_decimal,
									currScenario.currency_symbol,
									currScenario.currency_unit,
									true,
								)
							: "";
					},
					color: function (context) {
						if (context.tick.value > 0) {
							return mColor.Danger;
						} else if (context.tick.value < 0) {
							return mColor.Land;
						}
						return mColor.MidnightBlue;
					},
				},
			},
		},
	});
	const [chartBy, setChartBy] = useState("11"); // 111: minitary service, 112: domain
	const [chartByFy, setChartByFy] = useState(false);
	const [chartType, setCharType] = useState("bar");

	// Custom hook
	const { lookups } = useLookupsData();

	useEffect(() => {
		setCurrScenario(props.item);
	}, [props.item]);

	// update chartData
	useEffect(() => {
		let arrayData = [];
		let arrayLabel = [];
		let colors = [];
		let dataSets = [];

		// by Periods
		arrayData = currScenario.baseline_change_in_budget;
		arrayLabel = getValuesByPropertyPath(currScenario.cfg_period_label, "comp_value");

		// add Targeted Budget as a LINE chart
		if (parseInt(chartBy) === 11) {
			let targetBaseline = filterObjectsByProperty(currScenario.scenario_configs, "comp_type_id", 611);
			let arrayData2 = currScenario.baseline_total_budget.map((x, idx) => {
				let t = typeof targetBaseline[idx] !== "undefined" ? targetBaseline[idx].comp_value1 : 0;
				return x * 1 + t * 1;
			});
			const tempO2 = {
				label: "Targeted Budget",
				data: arrayData2,
				// valueFormatter,
				// you can set indiviual colors for each bar
				backgroundColor: mColor.StonyBlue,
				borderColor: mColor.SkyBlue,
				borderWidth: 2,
				type: "line",
				order: 0,
			};
			dataSets.push(tempO2);
		}

		// determine colors based on the changes (ex. red for increase and green for descrease)
		colors = arrayData.map((x) => {
			return x > 0 ? mColor.Danger : mColor.Land;
		});
		const tempO = {
			label: "Budget Changes",
			data: arrayData,
			// valueFormatter,
			// you can set indiviual colors for each bar
			backgroundColor: colors,
			borderColor: mColor.MistyBlue,
			borderWidth: 1,
			order: 1,
		};
		dataSets.push(tempO);

		setChartOptions((prev) => ({
			...prev,
			plugins: {
				...prev.plugins,
				title: { ...prev.plugins.title, text: "By Period" },
				legend: { ...prev.plugins.legend, display: true },
			},
		}));

		const byObj = findObjectByProperty(arrayGroupDetails, "comp_type_id", parseInt(chartBy));

		if (byObj && parseInt(chartBy) !== 11) {
			arrayData = [];
			dataSets = [];

			if (!chartByFy) {
				arrayLabel = [];
				colors = [];
				currScenario[`cfg_${byObj.code}`].map((x, idx) => {
					if (filterObjectsByProperty(currScenario.baseline_items, byObj.column, x.id).length > 0) {
						arrayData.push(x.change_in_budget.reduce((a, b) => a + b, 0));
						arrayLabel.push(x.comp_value);
						colors.push(x.color);
					}
					return false;
				});

				const tempO = {
					label: byObj.name,
					data: arrayData,
					// valueFormatter,
					// you can set indiviual colors for each bar
					backgroundColor: colors,
					borderColor: mColor.MistyBlue,
					borderWidth: 1,
				};
				dataSets.push(tempO);

				setChartOptions((prev) => ({
					...prev,
					plugins: {
						...prev.plugins,
						title: { ...prev.plugins.title, text: "By " + byObj.name },
						legend: { ...prev.plugins.legend, display: false },
					},
				}));
			} else {
				currScenario[`cfg_${byObj.code}`].map((x, idx1) => {
					if (filterObjectsByProperty(currScenario.baseline_items, byObj.column, x.id).length > 0) {
						// determine colors based on the changes (ex. red for increase and green for descrease)
						colors = x.change_in_budget.map((y) => {
							return y > 0 ? mColor.Danger : mColor.Land;
						});

						const tempO = {
							label: x.comp_value,
							data: x.change_in_budget,
							// valueFormatter,
							// you can set indiviual colors for each bar
							backgroundColor: x.color,
							borderColor: mColor.MistyBlue,
							borderWidth: 1,
						};
						if (chartType === "line") {
							tempO.borderColor = x.color;
							tempO.borderWidth = 2;
						} else {
							tempO.borderColor = mColor.White;
							tempO.borderWidth = 1;
						}
						dataSets.push(tempO);
					}

					setChartOptions((prev) => ({
						...prev,
						plugins: {
							...prev.plugins,
							title: { ...prev.plugins.title, text: `By ${byObj.name} / Period` },
							legend: { ...prev.plugins.legend, display: true },
						},
					}));
					return false;
				});
			}
		}

		let tempObj = {
			labels: arrayLabel,
			datasets: dataSets,
			colors: colors,
		};
		setChartData({ ...tempObj });

	}, [currScenario, chartBy, chartByFy, chartType]);

	// update deltaChartData
	useEffect(() => {
		let arrayData = [];
		let arrayLabel = [];
		let dataSets = [];

		// by Periods
		arrayData = currScenario.baseline_change_in_budget;
		arrayLabel = getValuesByPropertyPath(currScenario.cfg_period_label, "comp_value");

		arrayData = [];
		dataSets = [];
		// colors = [];
		const backgroundColor = ["#023047ff", "#219ebcff", "#8ecae6ff", "#ffb703ff", "#fb8500ff"];
		// items order changes if there's changes on the item options
		arrayData = [...currScenario.baseline_items];
		arrayData = arrayData.sort(
			(a, b) => Math.abs(b.item_change_in_budget_total) - Math.abs(a.item_change_in_budget_total),
		);
		arrayLabel = getValuesByPropertyPath(currScenario.cfg_period_label, "comp_value");

		arrayData.map((x, idx1) => {
			const tempO = {
				label: x.name + `\n(${x.option_type_name})`,
				data: x.item_change_in_budget,
				budgetTotal: x.item_change_in_budget_total,
				quantity: x.item_change_in_quantity,
				quantityTotal: x.item_change_in_quantity_total,
				periods: arrayLabel,
				// valueFormatter,
				// you can set indiviual colors for each bar
				backgroundColor: backgroundColor[idx1],
				borderColor: mColor.MistyBlue,
				borderWidth: 1,
			};

			if (idx1 < 3) dataSets.push(tempO);

			return false;
		});

		let tempObj = {
			labels: arrayLabel,
			datasets: dataSets,
			colors: backgroundColor,
		};
		setDeltaChartData({ ...tempObj });
	}, [currScenario]);

	const createSummaryTable = useCallback(() => {
		const summaryTable = refSummary.current;
		if (!summaryTable) return false;

		let summaryTableDiv = summaryTable.querySelector("#summaryTableDiv");
		if (summaryTableDiv) {
			summaryTableDiv.parentNode.removeChild(summaryTableDiv);
		}
		summaryTableDiv = document.createElement("DIV");
		summaryTableDiv.setAttribute("id", "summaryTableDiv");

		// --- Call the pure builder ---
		const table = buildSummaryTable({
			currScenario,
			deltaChartData,
			applyFormatToNumber,
			showTesting,
			filterObjectsByProperty,
			getValuesByPropertyPath,
		});

		summaryTableDiv.appendChild(table);
		summaryTable.appendChild(summaryTableDiv);
	}, [currScenario, deltaChartData]);

	const createDeltaTable = useCallback(() => {
		const deltaTable = refDelta.current;
		if (!deltaTable) return false;

		let chartTableDiv = deltaTable.querySelector("#chartTableDiv");
		if (chartTableDiv) {
			chartTableDiv.parentNode.removeChild(chartTableDiv);
		}
		chartTableDiv = document.createElement("DIV");
		chartTableDiv.setAttribute("id", "chartTableDiv");

		// Use the new helper:
		const table = buildDeltaTable({
			deltaChartData,
			currScenario,
			mColor,
			getContrastYIQ,
			applyFormatToNumber,
			applySignToNumber,
			showTesting,
		});
		chartTableDiv.appendChild(table);
		deltaTable.appendChild(chartTableDiv);
	}, [currScenario, deltaChartData]);

	const createChartTable = useCallback(() => {
		const chartTable = ref.current;
		if (!chartTable) return false;

		let tableDiv = chartTable.querySelector("#tableDiv");
		if (tableDiv) {
			tableDiv.parentNode.removeChild(tableDiv);
		}
		tableDiv = document.createElement("DIV");
		tableDiv.setAttribute("id", "tableDiv");

		// Call the builder
		const table = buildChartTable({
			chartData,
			chartBy,
			chartByFy,
			currScenario,
			applyFormatToNumber,
			showTesting,
			getContrastYIQ,
			mColor,
		});

		tableDiv.appendChild(table);
		chartTable.appendChild(tableDiv);
	}, [chartBy, chartByFy, chartData, currScenario]);

	// Rebuild the exported DOM tables only when their source data changes; this
	// keeps render-count logging useful without doing table DOM work every render.
	useEffect(() => {
		createSummaryTable();
	}, [createSummaryTable]);

	useEffect(() => {
		createDeltaTable();
	}, [createDeltaTable, showBiggestDeltas]);

	useEffect(() => {
		createChartTable();
	}, [createChartTable]);

	const showBiggestDeltasChangeHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] showBiggestDeltasChangeHandler", {
			next: !showBiggestDeltas,
		});
		setShowBiggestDeltas(!showBiggestDeltas);
	};

	const chartByChangeHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] chartByChangeHandler", {
			value: event?.target?.value,
		});
		setChartByFy(false);
		setChartBy(String(event.target.value));
		setCharType("bar");
	};

	const chartByFyChangeHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] chartByFyChangeHandler", {
			next: !chartByFy,
		});
		setChartByFy(!chartByFy);
		if (chartByFy) setCharType("bar");
	};

	const chartTypeChangeHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] chartTypeChangeHandler", {
			checked: event?.target?.checked,
		});
		setCharType(event.target.checked ? "line" : "bar");
	};

	const barClickHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] barClickHandler", {
			element: getElementsAtEvent(barRef.current, event),
		});
	};

	const exportPdfHandler = (event) => {
		debugLog(3, "[ScenarioBudgetSummary][ACTION] exportPdfHandler", {});
		debugLog(2, "[ScenarioBudgetSummary][FLOW] onExportPdfSummary started", {});
		const inputs = document.getElementsByClassName("hideFromExport");
		const blanks = document.getElementsByClassName("showWhenExport");

		for (let i = 0; i < inputs.length; i++) {
			inputs[i].style.display = "none";
		}
		for (let i = 0; i < blanks.length; i++) {
			blanks[i].style.display = "block";
		}
		props.onExportPdfSummary();
		for (let i = 0; i < inputs.length; i++) {
			inputs[i].style.display = "block";
		}
		for (let i = 0; i < blanks.length; i++) {
			blanks[i].style.display = "none";
		}
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioBudgetSummary][FLOW] render", {
		renderCount: renderCount.current,
		props: props,
	});
	// Existing Development Program (301):  Accelerate(402), Delay(401), Terminate(403)
	// Existing Procurement Program (302): Add/Cut(404), Cut(405 x), Terminate(403)
	// Existing Forces (303): Add/Cut(404), Cut(405 x)
	// Legacy System (304): Retire more/less(406), RETIRE LESS(407 x)
	// New Development Program (305): New develop(408)
	debugLog(9, "[ScenarioBudgetSummary][FLOW] renderState", {
		"lookups.BL_ITEM_OPTS": lookups.BL_ITEM_OPTS,
		chartBy: chartBy,
		chartByFy: chartByFy,
		chartType: chartType,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<Box p={1} mb={1} display="flex" flexWrap="wrap" justifyContent="center" w="80%">
			<Stack gap={12} p={0} w="100%">
				<div className="showWhenExport" style={{ display: "none" }}>
					<div className="flex-div left_480">
						<img
							className="main-header__logo"
							alt="Metrea"
							data-src="Metrea-Swift-Blue.png"
							src="/Metrea-Swift-Blue.png"
						/>
						{/* </a> */} <div className="main-header__logo-name">Raven</div>
					</div>
					<br />
				</div>
				<Box flex="1 1 100%" mt={10} display="flex" justifyContent="center">
					<div ref={refSummary} id="summaryTable" style={{ fontSize: "0.6rem" }}></div>
				</Box>

				<HStack>
					<Box flex="1 1 100%" p={10} display="flex" justifyContent="space-between">
						<Stack flex="1 1 90%">
							<Text fontWeight="bold">Display Options</Text>
							<Box
								display="grid"
								gridTemplateColumns="30% 30% 30%"
								columnGap={10}
								alignItems="start"
							>
								<Box p={10}>
									<RadioGroup.Root
										value={chartBy}
										size="sm"
										onValueChange={(val) =>
											chartByChangeHandler({ target: { value: String(val?.value) } })
										}
									>
										<VStack align="start" spacing="2">
											<RadioGroup.Item value="11">
												<RadioGroup.ItemHiddenInput />
												<RadioGroup.ItemIndicator />
												<RadioGroup.ItemText>Total</RadioGroup.ItemText>
											</RadioGroup.Item>

											<RadioGroup.Item value="111">
												<RadioGroup.ItemHiddenInput />
												<RadioGroup.ItemIndicator />
												<RadioGroup.ItemText>Military Service</RadioGroup.ItemText>
											</RadioGroup.Item>

											<RadioGroup.Item value="112">
												<RadioGroup.ItemHiddenInput />
												<RadioGroup.ItemIndicator />
												<RadioGroup.ItemText>Domain</RadioGroup.ItemText>
											</RadioGroup.Item>
										</VStack>
									</RadioGroup.Root>
								</Box>

								<Box
									p={10}
									display="flex"
									flexDirection="column"
									alignItems="flex-start"
									gap={10}
								>
									<Switch.Root
										size="sm"
										disabled={parseInt(chartBy) === 11}
										checked={chartByFy}
										onCheckedChange={(next) =>
											chartByFyChangeHandler({
												target: { checked: getCheckedValue(next) },
											})
										}
									>
										<Switch.HiddenInput />
										<Switch.Control>
											<Switch.Thumb />
										</Switch.Control>
										<Switch.Label>Group by Period</Switch.Label>
									</Switch.Root>
									<Switch.Root
										size="sm"
										checked={chartType === "line"}
										onCheckedChange={(next) =>
											chartTypeChangeHandler({
												target: { checked: getCheckedValue(next) },
											})
										}
										disabled={parseInt(chartBy) === 11}
										visibility={chartByFy ? "visible" : "hidden"}
									>
										<Switch.HiddenInput />
										<Switch.Control>
											<Switch.Thumb />
										</Switch.Control>
										<Switch.Label>Show Line Chart</Switch.Label>
									</Switch.Root>
								</Box>

								<Box p={10} display="flex" flexDirection="column" alignItems="flex-start">
									<Switch.Root
										size="sm"
										checked={showBiggestDeltas}
										onCheckedChange={(next) =>
											showBiggestDeltasChangeHandler({
												target: { checked: getCheckedValue(next) },
											})
										}
									>
										<Switch.HiddenInput />
										<Switch.Control>
											<Switch.Thumb />
										</Switch.Control>
										<Switch.Label>Show Biggest Deltas</Switch.Label>
									</Switch.Root>
								</Box>
							</Box>
						</Stack>

						{typeof props.onLoad === "undefined" && (
							<MetreaTooltip
								content="Download PDF Report"
							>
								<Button size="xs" onClick={exportPdfHandler}>
									Export PDF
								</Button>
							</MetreaTooltip>
						)}
					</Box>
				</HStack>

				{chartType === "bar" && parseInt(chartBy) === 11 && (
					<Bar ref={barRef} data={chartData} options={chartOptions} onClick={barClickHandler} />
				)}
				{chartType === "bar" && parseInt(chartBy) !== 11 && (
					<Bar ref={barRef} data={chartData} options={chartOptions} onClick={barClickHandler} />
				)}
				{chartType === "line" && <Line data={chartData} options={chartOptions} />}

				<Box flex="1 1 100%" mt={10} display="flex" justifyContent="center">
					<div ref={ref} id="chartTable" style={{ fontSize: "0.6rem" }}></div>
				</Box>

				<div className="pageBreak" />

				{showBiggestDeltas && (
					<>
						<Bar data={deltaChartData} options={deltaChartOptions} />

						<Box flex="1 1 100%" mt={10} display="flex" justifyContent="center">
							<div ref={refDelta} id="deltaTable" style={{ fontSize: "0.6rem" }}></div>
						</Box>
						<div className="pageBreak" />
					</>
				)}
			</Stack>
		</Box>
	);
};

export default ScenarioBudgetSummary;
